import { auth } from '../../utils/auth';
import { AuthState, AuthStateLoginStatus } from '../../types/auth';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { cloneDeep } from 'lodash-es';
import { dispatch } from '../store';
import { dispatchAuthUpdatedEvent } from '../../auth/auth-utils';
import { EMPTY_ACTION } from './emptyAction';
import { Epic, ofType } from 'redux-observable';
import { getCompanyDetails, getCompanyUserDetails } from '../../apis/profile';
import { of } from 'rxjs';
import { postAuthMessage } from '../../broadcast-channels/authBroadcast';
import { SetAuthIdentityPayload } from '../../types/auth-state';
import {
  onAuthChange,
  onAuthInit,
  onAuthLogin,
  onAuthLogout,
  setAuthIdentity,
  unsetAuthIdentity,
} from '../slice/authSlice';

const unsetPayload: SetAuthIdentityPayload = {
  identity: null,
  loginStatus: AuthStateLoginStatus.NOT_AUTHENTICATED,
  isOwner: false,
};

export const authIdentityEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(onAuthInit.type, onAuthChange.type),
    tap(() => {
      // Dispatch the loading state before performing auth checks
      dispatch(
        setAuthIdentity({
          identity: null,
          loginStatus: AuthStateLoginStatus.LOADING,
          isOwner: false,
        }),
      );
    }),
    switchMap(async () => {
      const authState: AuthState = cloneDeep(state$.value.auth);
      try {
        const isLoading = auth.isLoading();
        if (isLoading) {
          return EMPTY_ACTION('Is loading auth details');
        }

        /*  
          If not authenticated, unset the application auth state  
        */
        const isAuthenticated = auth.isAutheticated();
        if (!isAuthenticated) {
          postAuthMessage(unsetPayload);
          if (authState.lastLoginStatus === AuthStateLoginStatus.AUTHENTICATED) {
            dispatch(onAuthLogout());
          }
          return unsetAuthIdentity();
        }

        const identity = await auth.getAuthIdentity();
        if (!identity) {
          return EMPTY_ACTION('Unable to get user identity details');
        }

        const [companyDetails, userDetails] = await Promise.all([
          getCompanyDetails(identity.companyId),
          getCompanyUserDetails(identity.userId, identity.companyId),
        ]);
        const isOwner = identity.userId === companyDetails.owner_user_id;

        const payload: SetAuthIdentityPayload = {
          identity: {
            ...identity,
            readOnly: userDetails.read_only,
            securityLevelId: userDetails.security_level_id,
            companyName: companyDetails.name,
            phoneNumber: userDetails.phone_number || '',
          },
          loginStatus: AuthStateLoginStatus.AUTHENTICATED,
          isOwner,
        };

        postAuthMessage(payload);
        if (authState.lastLoginStatus !== AuthStateLoginStatus.AUTHENTICATED) {
          dispatch(onAuthLogin());
        }
        return setAuthIdentity(payload);
      } catch (error) {
        postAuthMessage(unsetPayload);
        if (authState.lastLoginStatus === AuthStateLoginStatus.AUTHENTICATED) {
          dispatch(onAuthLogout());
        }
        return unsetAuthIdentity();
      }
    }),
    catchError(() => {
      postAuthMessage(unsetPayload);
      dispatch(onAuthLogout());
      return of(unsetAuthIdentity());
    }),
  );

/**
 * Epic responsible for dispatching auth update event when the auth state changes.
 * Listened actions: Actions responsible for updating auth state.
 */
export const authEventEpic: Epic = (action$) =>
  action$.pipe(
    ofType(setAuthIdentity.type, unsetAuthIdentity.type),
    map(() => {
      dispatchAuthUpdatedEvent();
      return EMPTY_ACTION('From authEventEpic');
    }),
    catchError(() => {
      return of(EMPTY_ACTION('Error at authEventEpic'));
    }),
  );
